import { useState, useEffect, useRef, useCallback } from 'react';
import { BRAND } from '../config/brand';
import { getAttendanceConfig, kioskMark, DEFAULT_ATT } from '../lib/attendance';
import QrScreen from './QrScreen';

const fmtClock = (d) => d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
const fmtHM = (iso) => new Date(iso).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });

// Tablet fija en la entrada: el empleado marca con su código personal de 6 dígitos.
export default function KioskView({ onLogout }) {
  const [cfg, setCfg] = useState(DEFAULT_ATT);
  const [loaded, setLoaded] = useState(false);
  const [now, setNow] = useState(new Date());
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {ok, ...}
  const idle = useRef(null);

  useEffect(() => { getAttendanceConfig().then((c) => { setCfg(c); setLoaded(true); }).catch(() => setLoaded(true)); }, []);
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  useEffect(() => {
    let lock;
    const ask = async () => { try { lock = await navigator.wakeLock?.request('screen'); } catch (e) { /* no disponible */ } };
    ask();
    const onVis = () => { if (document.visibilityState === 'visible') ask(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { document.removeEventListener('visibilitychange', onVis); try { lock?.release(); } catch (e) { /* nada */ } };
  }, []);

  // Si alguien deja un código a medias, se borra
  useEffect(() => {
    clearTimeout(idle.current);
    if (pin) idle.current = setTimeout(() => setPin(''), 15000);
    return () => clearTimeout(idle.current);
  }, [pin]);

  const submit = useCallback(async (code) => {
    setBusy(true);
    try {
      const r = await kioskMark(code);
      setResult(r);
    } catch (e) {
      setResult({ ok: false, error: e.message || 'No se pudo registrar' });
    }
    setBusy(false);
    setPin('');
    setTimeout(() => setResult(null), 4500);
  }, []);

  const press = (d) => {
    if (busy || result) return;
    const next = (pin + d).slice(0, 6);
    setPin(next);
    if (next.length === 6) submit(next);
  };

  const first = (result?.name || '').split(' ')[0];
  const keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'C', '0', '⌫'];

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-5 py-6 select-none" style={{ background: 'linear-gradient(135deg,#0f172a 0%,#1e293b 100%)' }}>
      <div className="text-center mb-4">
        {BRAND.orgLogo && <img src={BRAND.orgLogo} alt="" className="h-12 mx-auto mb-2 rounded-2xl bg-white p-1" />}
        <h1 className="text-white text-xl font-bold">{BRAND.orgName || BRAND.product}</h1>
        <p className="text-white text-5xl font-black tabular-nums mt-2">{fmtClock(now)}</p>
        <p className="text-white/50 text-sm mt-1 capitalize">{now.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
      </div>

      {!loaded ? null : !cfg.methods.kiosk && !cfg.methods.qr_screen ? (
        <div className="bg-white/10 text-white rounded-3xl p-6 max-w-sm text-center text-sm">El administrador todavía no activó el modo kiosco ni el QR para esta organización.</div>
      ) : (
        <div className="flex flex-col lg:flex-row items-center gap-8">
          {cfg.methods.kiosk && (
            <div className="w-full max-w-xs">
              {result ? (
                <div className={`rounded-3xl p-8 text-center ${result.ok ? 'bg-emerald-500' : 'bg-red-500'} text-white`}>
                  {result.ok ? (
                    <>
                      <div className="text-6xl mb-3">{result.action === 'in' ? '👋' : '🏁'}</div>
                      <p className="text-2xl font-black">{result.action === 'in' ? `¡Hola, ${first}!` : `¡Hasta luego, ${first}!`}</p>
                      <p className="text-sm mt-2 opacity-90">{result.action === 'in' ? 'Entrada' : 'Salida'} registrada a las {fmtHM(result.at)}</p>
                      {result.action === 'in' && result.minutes_late > 0 && <p className="text-sm mt-1 opacity-90">Llegaste {result.minutes_late} min tarde</p>}
                      {result.action === 'out' && result.minutes_worked != null && <p className="text-sm mt-1 opacity-90">Trabajaste {Math.floor(result.minutes_worked / 60)}h {result.minutes_worked % 60}m</p>}
                    </>
                  ) : (
                    <>
                      <div className="text-5xl mb-3">⚠️</div>
                      <p className="text-lg font-bold">{result.error}</p>
                    </>
                  )}
                </div>
              ) : (
                <div className="bg-white/10 rounded-3xl p-5">
                  <p className="text-white/70 text-sm text-center mb-3">Ingresá tu código personal</p>
                  <div className="flex justify-center gap-2.5 mb-4">
                    {[0, 1, 2, 3, 4, 5].map((i) => <span key={i} className={`w-3.5 h-3.5 rounded-full ${i < pin.length ? 'bg-white' : 'bg-white/25'}`} />)}
                  </div>
                  <div className="grid grid-cols-3 gap-2.5">
                    {keys.map((k) => (
                      <button key={k} disabled={busy} onClick={() => (k === 'C' ? setPin('') : k === '⌫' ? setPin(pin.slice(0, -1)) : press(k))}
                        className="py-4 rounded-2xl text-2xl font-bold text-white bg-white/10 active:bg-white/30 disabled:opacity-40">{k}</button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          {cfg.methods.qr_screen && (
            <div className="w-full max-w-xs">
              <p className="text-white/70 text-sm text-center mb-2">o escaneá el QR con tu celular</p>
              <QrScreen compact />
            </div>
          )}
        </div>
      )}

      <button onClick={() => window.confirm('¿Cerrar la sesión de este kiosco?') && onLogout()} className="mt-8 text-[11px] text-white/30 px-3 py-1.5">Cerrar sesión del kiosco</button>
    </div>
  );
}
